import { ShareControlModeString } from '../../share-control/share-control.service';
import { PiiString } from '../instrumentation/instrumentation.service';

// TODO: Should get d.ts files from WAC
export interface OfficeBootstrapperContainer {
    Word?: OfficeBootstrapper;
    Excel?: OfficeBootstrapper;
    PowerPoint?: OfficeBootstrapper;
}

export interface OfficeBootstrapper {
    Application: OfficeBootstrapperApp;
}

export interface OfficeBootstrapperApp {
    InitializeWopiPending(
        initParams: OfficeBootstrapperInitParams,
        loggerToUse?: any, // Not currently used in jsapi v1
        sessionIdToUse?: string
    ): WopiPendingApp | undefined;
}

export interface NewFileTemplateSource {
    /** File url of the template. */
    FileGetUrl: string;
    /** File size of the template. */
    FileSize?: number;
    /** ETag for the file. */
    ETag?: string;
    /** Unique ID of the document. */
    DocUniqueId?: string;
}

// true for onflight, false for control, undefined for not on flight
export type IsFeatureEnabledCallback = (featureName: string) => boolean | undefined;

export interface OfficeBootstrapperInitParams {
    BootstrapperUrl: String;
    Container: HTMLElement; // whatever HTML Element
    SessionInformation: OfficeBootstrapperSessionInfo;
    ApplicationUrl: string;
    WopiPrecheckInfo: OfficeBootstrapperWopiPrecheckInfo;
    ApplicationCustomSettings: OfficeApplicationCustomSettings;
    Diagnostics: HostInitializeDiagnostics;
    FnOnInitializeSuccess: OfficeBootstrapperSuccessCallback;
    FnOnInitializeFailure: OfficeBootstrapperFailureCallback;
    FnIsFeatureEnabled: IsFeatureEnabledCallback;
    WopiInfo?: WopiInfo;
    RebootOverrideData?: RebootOverrideData;
}

export type OfficeBootstrapperSuccessCallback = (app: OfficeApplication, diagnostics: OfficeApplicationDiagnostics) => void;

export type OfficeBootstrapperFailureCallback = (error: Error, diagnostics?: OfficeApplicationDiagnostics) => void;

export interface OfficeApplicationCustomSettings {
    HostUIControls?: Array<OfficeApplicationCustomUIControl>;
    appFrameName?: string;
}

export interface OfficeApplicationCustomUIControl {
    id: string;
    visibility: number; // 0 or 1
}

export interface OfficeBootstrapperSessionInfo {
    HostName: string;
    HostSessionId: string;
    UserId: string;
    OwnerId: string; // not important for now, will be more important later
    UiLocale: string; // the language of the current browser session
    DataLocale: string; // can be the same as the UI local and it's ok for most of the cases
    DisableChat: boolean;  // this will disable the document chat for now. we might want to enable it later on
    CookieCompliant: boolean; // means we already get the user consent to store cookies - for now true, but will need to figure out from the compliance standpoint when going in prod
    WdParams: any; // used to pass-though the wd-params. not sure we really needed, but let's keep it
    DisableFocusOnBoot: boolean; // not used
    HighContrastMode?: boolean; // not intended to be used in browsers
    SessionContext?: string; // session context as a string
    HideHeader?: boolean;
    SupportsDocReboot?: boolean | DocRebootSupportMode;
    SupportsCreateNewDocument?: boolean;
    SupportsAuthToken?: boolean;
    UiHostIntegrationType?: string;
    SupportsAtMentions?: boolean;
    SupportsCustomAuthConfig?: boolean;
    SupportsExcelExternalLinks?: boolean;
    SupportsMoveToFolder?: boolean;
}

export enum DocRebootSupportMode {
    NotSupported = 0,
    SameDocOnly = 1,
    // tslint:disable-next-line
    AnyDoc = 1 << 1,
    // tslint:disable-next-line
    OtherMode = 1 << 2
}

export interface RebootOverrideDataBase {
    previousSessionId: string;
    mode: 'view' | 'edit';
    isNewSession?: boolean;
}

export interface RebootOverrideData extends RebootOverrideDataBase {
    queryOverrides?: { [key: string]: string };
}

export interface RebootOverrideDataMessage extends RebootOverrideDataBase {
    queryOverrides?: string;
}

export interface OfficeBootstrapperWopiPrecheckInfo {
    FileName: string;
    FileGetUrl?: string;
    FileSize?: number;
    BundleInfo?: {
        Url: string;
        MajorVersion: string;
    };
    ReadOnly?: boolean;
    IrmEnabled?: boolean;
    ETag?: string;
    IsNewFile?: boolean;
    RequestedCallThrottling?: string;
    ClientThrottlingProtection?: string;
    // Required for powerpoint
    DocUniqueId?: string;
}

export interface HostInitializeDiagnostics {
    /* In Hosted mode:
    B0 – user click timestamp
    B1 – tracks when the client is ready to create the hosted window (it may be in parallel with the VROOM API, so always record this stamp first)
    B2 – client time of invoking the VROOM API to bootstrap the Hosted session (if done sequentially after user-click, otherwise set equal to B1)
    B3 – client time of getting the VROOM API response (if done sequentially after user-click, otherwise set equal to B2)
    B4 – tracks when the client is ready to begin bootstrapping (aka when BOTH the window to host in is ready and the VROOM API is complete)
    B5 – when the client starts fetching the JsApi
    B6 – when the client is done receiving the JsApi
    B7 – when the client is ready to invoke the magic command inside the JsApi to launch the WAC stuff in the child window */
    entryPoint?: string;
    userClickTime?: number; // B0
    hostPageNavigationStartTime?: number; // B1
    hostPageFetchStartTime?: number; // B2
    hostPageFetchEndTime?: number; // B3
    hostPageFirstFlushTime?: number; // B4
    officeBootstrapperStartTime?: number; // B5
    officeBootstrapperEndTime?: number; // B6
    officeInitializationTime?: number; // B7
}

export interface WopiPendingApp {
    Complete: (wopiInfo: WopiInfo, newFileInfo?: NewFileInfo) => void;
}

export interface WopiInfo {
    WopiSrc: string;
    AccessToken: string;
    AccessTokenExpiry: Date;
}

/**
 * Details of the custom template to be used to create the new file.
 */
// tslint:disable-next-line:interface-name
export interface INewFileTemplateSource {
    /** File url of the template. */
    FileGetUrl: string;
    /** File size of the template. */
    FileSize?: number;
    /** ETag for the file. */
    ETag?: string;
    /** Unique ID of the document. */
    DocUniqueId?: string;
}

/**
 * Once information about the new file is gathered, INewFileInfo can be optionally passed into
 * IWopiPendingApplication.Complete. The bootstrapper then handles updates to the browser, such as
 * setting the url to the new bookmark-able url, and updating the page title
 */
// tslint:disable-next-line:interface-name
export interface NewFileInfo {
    /**
     * If the user has created a new document, this is the bookmark-friendly path to the new file,
     * The browser url will be updated by the bootstrapper, based on the user's current path
     */
    NewFilePath: string;

    /**
     * The new page title to use for the created document (if any).
     */
    UpdatedPageTitle?: string;

    /**
     * Details of the template selected by user to create a new file (if any)
     */
    TemplateSource?: INewFileTemplateSource;
}

export interface OfficeApplication {
    applicationName: string;
    sessionId: string;
    version: string;
    focus(): void;
    blur(): void;
    renameFile(requestedName: string): void;
    RegisterErrorHandler: (handler: (error: Error) => void) => void;
    RegisterEventHandler: (eventName: string, handler: OfficeApplicationEventHandler) => void;
    RemoveErrorHandler: (handler: (error: Error) => void) => void;
    RemoveEventHandler: (eventName: string, handler: OfficeApplicationEventHandler) => void;
    Dispose: () => void;
    GetVersion: () => any;
}

export type OfficeApplicationEventHandler = (args: OfficeApplicationEventArgs) => void;

export interface UserAccessPerson {
    Id: string;
    Provider: string;
    DisplayName: string;
}

export const enum ApplicationUrlParam {
    Nav = 'actnavid'
}

export type OfficeApplicationDiagnostics = any;

export const enum OfficeUIEvents {
    FileRenamed = 'File_Rename',
    Close = 'UI_Close',
    EditModeSwitch = 'UI_Edit',
    FileVersion = 'UI_FileVersions',
    SaveStatus = 'UI_SaveStatus',
    Share = 'UI_Sharing',
    Workflow = 'UI_Workflow',
    UserActivity = 'User_Activity',
    EditNotification = 'Edit_Notification',
    CheckUserAccess = 'UI_CheckUserAccessAndGrantPermissions',
    /**
     * Event fired when a document reboot is requested from the app
     */
    DocumentReboot = 'DocReboot',
    PostMessageDocReboot = 'App_DocReboot',
    AppRefreshSessionInfo = 'App_RefreshSessionInfo',
    AppCreateNewDocument = 'App_CreateNewDocument',
    SetOverlayContent = 'SetOverlayContent',
    Unloaded = 'Unloaded',
    Disposed = 'Disposed',
    GetAuthToken = 'GetAuthToken',
    GetWopiInfoFromUrl = 'WopiInfoFromUrl',
    GetCustomAuthConfig = 'GetCustomAuthConfig',
    MoveToFolder = 'MoveToFolder' //should we use MoveToFolder or App_MoveToFolder??
}

export const enum OfficeUIPostMessages {
    RefreshSessionInfo = 'Host_RefreshSessionInfo',
    SetAuthToken = 'Host_SetAuthToken',
    SetCustomAuthConfig = 'Host_SetCustomAuthConfig',
    GrantPermissions = 'CheckUserAccessAndGrantPermissions_Result',
    SetOverlayContent = 'SetOverlayContent',
    SetHostWopiInfoFromUrl = 'Host_WopiInfoFromUrl',
    HostMoveToFolder = 'Host_MoveToFolder'
}

export interface OfficeApplicationEventArgs {
    wdUserSession: string;
}

export interface OfficeApplicationShareEventArgs extends OfficeApplicationEventArgs {
    nav?: string;
    mode?: ShareControlModeString;
}

export interface OfficeApplicationRebootEventArgs extends OfficeApplicationEventArgs, RebootOverrideDataMessage {
    rebootMode: RebootMode;
    documentUrl: string;
}

export const enum RebootMode {
    SameDocument = '0',
    NewDocument = '1'
}

export interface OfficeApplicationUserAccessEventArgs extends OfficeApplicationEventArgs {
    Id: number;
    Persons: Array<UserAccessPerson>;
    UserPermissionsVerified: true;
}

export interface OfficeApplicationRefreshSessionEventArgs extends OfficeApplicationEventArgs {
    Tokens: Array<RefreshTokenType>;
}

export interface OfficeApplicationSetOverlayContentEventArgs extends OfficeApplicationEventArgs {
    overlayContent: boolean;
}

export interface OfficeApplicationCreateNewEventArgs extends OfficeApplicationEventArgs {
    NewDocumentDestinationHost?: string;
    template: string; // blank is normal file
    appId: 'WordWAC' | 'ExcelWAC' | 'PowerpointWAC';
    lcid: string;
    clickTimestamp?: string;
    buttonId?: string;
    canary?: string;
    sessionIdQueryParam?: string;
    logHostRequestQueryParam?: string;
    destinationUrlParamName?: string;
    personalFileStorageUrl?: string;
}

export interface OfficeApplicationGetCustomAuthConfigEventArgs extends OfficeApplicationEventArgs {
    RequestId?: string;
}

export enum RefreshTokenType {
    AccessToken = 'AccessToken',
    FileGetUrl = 'FileGetUrl'
}

export type SessionRefreshResponse = {
    Name: string;
    Expiry: string;
    Value: string;
} | {
    Name: string;
    Errors: Array<{
        Id: string;
        Details: string | PiiString;
    }>
};

export interface WopiPostMessageBase {
    MessageId: OfficeUIEvents;
    SendTime: number;
    CorrelationId: string;
    Values: OfficeApplicationEventArgs;
}

export interface ShareEventMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.Share;
    Values: OfficeApplicationShareEventArgs;
}

export interface RebootEventMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.DocumentReboot | OfficeUIEvents.PostMessageDocReboot;
    Values: OfficeApplicationRebootEventArgs;
}

export interface SaveStatusMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.SaveStatus;
}

export interface FileRenamedMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.FileRenamed;
}

export interface UserAccessMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.CheckUserAccess;
    Values: OfficeApplicationUserAccessEventArgs;
}

export interface RefreshSessionInfoMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.AppRefreshSessionInfo;
    Values: OfficeApplicationRefreshSessionEventArgs;
}

export interface CreateNewMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.AppCreateNewDocument;
    Values: OfficeApplicationCreateNewEventArgs;
}

export interface SetOverlayContentMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.SetOverlayContent;
    Values: OfficeApplicationSetOverlayContentEventArgs;
}

export interface UserActivityMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.UserActivity;
}

export interface GetCustomAuthConfigMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.GetCustomAuthConfig;
}

export interface OfficeApplicationGetAuthTokenEventArgs extends OfficeApplicationEventArgs {
    RequestId: string;
    Resource: string;
    Scope: string;
    IdentityType: string;
    Upn: string;
    SilentLogin: boolean;
    Claims: any;
    CorrelationId: string;
    ClientId: string;
}
export interface GetAuthTokenEventMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.GetAuthToken;
    Values: OfficeApplicationGetAuthTokenEventArgs;
}

export interface GetWopiInfoFromUrlEventMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.GetWopiInfoFromUrl;
    Values: OfficeApplicationGetWopiInfoFromUrlEventArgs;
}

export interface OfficeApplicationGetWopiInfoFromUrlEventArgs extends OfficeApplicationEventArgs {
    Url: string;
}

export interface MoveToFolderEventMessage extends WopiPostMessageBase {
    MessageId: OfficeUIEvents.MoveToFolder;
    Values: OfficeApplicationMoveToFolderEventArgs;
}

export interface OfficeApplicationMoveToFolderEventArgs extends OfficeApplicationEventArgs {
    uilanguage: number;
    supportsCrossSiteMove: boolean;
    moveStartTime: string;
}

export type WopiPostMessagesEvents =
    ShareEventMessage |
    RebootEventMessage |
    SaveStatusMessage |
    FileRenamedMessage |
    UserAccessMessage |
    RefreshSessionInfoMessage |
    CreateNewMessage |
    SetOverlayContentMessage |
    UserActivityMessage |
    GetAuthTokenEventMessage |
    GetCustomAuthConfigMessage |
    GetWopiInfoFromUrlEventMessage |
    MoveToFolderEventMessage;
